import { type StoryFeed, StoryFeedType } from '@/@types/story'
import type { User } from '@/@types/user'
import { motion, useReducedMotion, type Variants } from 'motion/react'
import { useEffect, useRef, useState } from 'react'
import StoryFeedItem, { getStoryFeedItemKey } from './StoryFeedItem'
import StoryFeedScrollButton from './StoryFeedScrollButton'
import styles from './StoryFeedList.module.css'

export interface StoryFeedListProps {
	currentUser: User
	feeds: StoryFeed[]
	loading?: boolean
	failed?: boolean
	onItemClick: (feed: StoryFeed) => void
	onRetry?: () => void
}

const ITEM_STEP = 84
const CURRENT_USER_STORY_FEED_KEY = 'CURRENT_USER_STORY_FEED'
const STORY_FEED_ITEM_STAGGER_SECONDS = 0.06
const STORY_FEED_LIST_VARIANTS: Variants = {
	hidden: {},
	visible: {
		transition: {
			staggerChildren: STORY_FEED_ITEM_STAGGER_SECONDS,
		},
	},
}

export default function StoryFeedList({
	currentUser,
	feeds,
	loading = false,
	failed = false,
	onItemClick,
	onRetry,
}: StoryFeedListProps) {
	const viewportRef = useRef<HTMLDivElement>(null)
	const [canScrollBackward, setCanScrollBackward] = useState(false)
	const [canScrollForward, setCanScrollForward] = useState(false)
	const prefersReducedMotion = useReducedMotion()
	const currentUserFeed: StoryFeed = { type: StoryFeedType.USER_STORY, user: currentUser }

	const updateScrollButtons = () => {
		const viewport = viewportRef.current

		if (!viewport) {
			return
		}

		const maxScrollLeft = viewport.scrollWidth - viewport.clientWidth

		setCanScrollBackward(viewport.scrollLeft > 1)
		setCanScrollForward(viewport.scrollLeft < maxScrollLeft - 1)
	}

	const handleScrollBackward = () => {
		viewportRef.current?.scrollBy({ left: -ITEM_STEP })
	}

	const handleScrollForward = () => {
		viewportRef.current?.scrollBy({ left: ITEM_STEP })
	}

	useEffect(() => {
		const viewport = viewportRef.current

		if (!viewport) {
			return
		}

		const resizeObserver = new ResizeObserver(updateScrollButtons)

		updateScrollButtons()
		resizeObserver.observe(viewport)
		viewport.addEventListener('scroll', updateScrollButtons, { passive: true })

		return () => {
			resizeObserver.disconnect()
			viewport.removeEventListener('scroll', updateScrollButtons)
		}
	}, [feeds.length, failed, loading])

	return (
		<section
			className={styles.root}
			aria-label="日记"
			aria-busy={loading}
			data-can-scroll-backward={canScrollBackward || undefined}
			data-can-scroll-forward={canScrollForward || undefined}
		>
			<div ref={viewportRef} className={styles.viewport}>
				{!loading && (
					<motion.ul
						className={styles.list}
						variants={STORY_FEED_LIST_VARIANTS}
						initial={prefersReducedMotion ? false : 'hidden'}
						animate="visible"
					>
						<StoryFeedItem
							key={CURRENT_USER_STORY_FEED_KEY}
							feed={currentUserFeed}
							currentUsername={currentUser.username}
							onClick={onItemClick}
						/>
						{feeds.map((feed) => (
							<StoryFeedItem
								key={getStoryFeedItemKey(feed)}
								feed={feed}
								currentUsername={currentUser.username}
								onClick={onItemClick}
							/>
						))}
						{failed && (
							<li className={styles.errorState} role="status">
								<span>其他日记加载失败</span>
								{onRetry && (
									<button type="button" className={styles.retryButton} onClick={onRetry}>
										重新加载
									</button>
								)}
							</li>
						)}
					</motion.ul>
				)}
			</div>
			{canScrollBackward && (
				<StoryFeedScrollButton direction="backward" onClick={handleScrollBackward} />
			)}
			{canScrollForward && (
				<StoryFeedScrollButton direction="forward" onClick={handleScrollForward} />
			)}
		</section>
	)
}
